import {
  Box,
  BoxProps,
  Center,
  Circle,
  Collapse,
  IconButton,
  Image,
  Stack,
  Text,
} from '@chakra-ui/react';
import * as React from 'react';
import { Draggable } from 'react-beautiful-dnd';
import { Controller, UseFormReturn, useWatch } from 'react-hook-form';

import { ImageFallbackIcon } from '@/imports/ui/chakra/components/ImageFallback/components/ImageFallbackIcon';
import { ChevronRightIcon, ChevronUpIcon } from '@/imports/ui/chakra/feather';
import { WebsiteBuilderPageFormValues } from '@/modules/website-builder/types';

import { DeleteButton } from '../../ContentItem/components/DeleteButton';
import { ImageAlternativeText } from '../../ImageAlternativeText';

export const ImageGroupImageItem: React.FC<{
  parentIndex: number;
  index: number;
  imageProxy: string | null;
  isExpanded: boolean;
  onToggleExpand: () => void;
  onEdit: () => void;
  onDelete: () => void;
  methods: UseFormReturn<WebsiteBuilderPageFormValues, any>;
  pointerEvents: BoxProps['pointerEvents'];
}> = (props) => {
  const { control } = props.methods;
  const defaultName = `Image ${props.index + 1}`;
  const namePath =
    `content.${props.parentIndex}.images.${props.index}.name` as const;

  const name =
    useWatch({
      control,
      name: namePath,
    }) || defaultName;

  const imageProxy =
    useWatch({
      control,
      name: `content.${props.parentIndex}.images.${props.index}.imageProxy`,
    }) || props.imageProxy;

  return (
    <Draggable draggableId={props.index.toString()} index={props.index}>
      {(provided, snapshot) => {
        return (
          <Box
            border="1px"
            borderColor="gray.200"
            borderRadius="xl"
            shadow={snapshot.isDragging ? 'lg' : 'sm'}
            bg="white"
            mb="2"
            ref={provided.innerRef}
            style={provided.draggableProps.style}
            {...provided.draggableProps}
            pointerEvents={props.pointerEvents}
          >
            <Stack direction="row" alignItems="center" spacing="0">
              <Center
                {...provided.dragHandleProps}
                px="4"
                alignSelf="stretch"
                cursor="grab"
              >
                <Stack spacing="1">
                  <Stack direction="row" spacing="1">
                    <Circle size="2px" bg="gray.500" />
                    <Circle size="2px" bg="gray.500" />
                  </Stack>
                  <Stack direction="row" spacing="1">
                    <Circle size="2px" bg="gray.500" />
                    <Circle size="2px" bg="gray.500" />
                  </Stack>
                  <Stack direction="row" spacing="1">
                    <Circle size="2px" bg="gray.500" />
                    <Circle size="2px" bg="gray.500" />
                  </Stack>
                </Stack>
              </Center>

              <Stack
                direction="row"
                flex="1"
                alignItems="center"
                spacing="3"
                py="2"
                pr="1"
                cursor="pointer"
                onClick={props.onToggleExpand}
                _hover={{ bg: 'gray.50' }}
                minW="0"
              >
                <Box
                  h="10"
                  sx={{ aspectRatio: '4 / 3' }}
                  borderRadius="md"
                  overflow="hidden"
                  flexShrink={0}
                  cursor="pointer"
                  title="Edit image"
                  onClick={(event) => {
                    event.stopPropagation();
                    props.onEdit();
                  }}
                >
                  {imageProxy ? (
                    <Image
                      src={imageProxy}
                      alt=""
                      h="full"
                      w="full"
                      objectFit="cover"
                    />
                  ) : (
                    <Center bg="gray.300" h="full" w="full">
                      <ImageFallbackIcon color="white" boxSize={4} />
                    </Center>
                  )}
                </Box>

                <Text textStyle="bodyStrong" flex="1" noOfLines={1}>
                  {name}
                </Text>

                <IconButton
                  variant="ghost"
                  colorScheme="gray"
                  size="sm"
                  icon={
                    props.isExpanded ? <ChevronUpIcon /> : <ChevronRightIcon />
                  }
                  aria-label={
                    props.isExpanded
                      ? 'Collapse alternative text'
                      : 'Expand alternative text'
                  }
                  onClick={(event) => {
                    event.stopPropagation();
                    props.onToggleExpand();
                  }}
                />
              </Stack>

              <Center px="3">
                <Box onClick={(event) => event.stopPropagation()}>
                  <DeleteButton
                    onDelete={props.onDelete}
                    namePath={namePath}
                    defaultName={defaultName}
                    methods={props.methods}
                  />
                </Box>
              </Center>
            </Stack>

            <Collapse in={props.isExpanded} animateOpacity unmountOnExit={false}>
              <Box
                px="4"
                pb="5"
                pt="1"
                onClick={(event) => event.stopPropagation()}
                onMouseDown={(event) => event.stopPropagation()}
              >
                <Controller
                  control={control}
                  name={`content.${props.parentIndex}.images.${props.index}.isDecorative`}
                  defaultValue={true}
                  render={({ field: decorativeField }) => (
                    <Controller
                      control={control}
                      name={`content.${props.parentIndex}.images.${props.index}.alternativeText`}
                      defaultValue={null}
                      render={({ field: alternativeTextField }) => (
                        <ImageAlternativeText
                          alternativeText={alternativeTextField.value ?? ''}
                          onAlternativeTextChange={(value) =>
                            alternativeTextField.onChange(value || null)
                          }
                          isDecorative={decorativeField.value ?? true}
                          onIsDecorativeChange={decorativeField.onChange}
                          imageProxy={imageProxy}
                        />
                      )}
                    />
                  )}
                />
              </Box>
            </Collapse>
          </Box>
        );
      }}
    </Draggable>
  );
};
